import type { UUID } from '@cmg/common';
import type { FC } from 'react';
import { useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { useDocumentTitle } from '../../../../common/hooks/useDocumentTitle/useDocumentTitle';
import routeFactory from '../../../../common/util/routeFactory';
import { ServerErrorAlert } from '../../../../design-system/alert/ServerErrorAlert';
import { IdentityPageContent } from '../../../../design-system/IdentityPageContent';
import { selectAccount, selectLoading } from '../../../account-detail/shared/ducks';
import { selectSelfSubdomain } from '../../../shared/ducks';
import { selectCrmIntegration } from '../../shared/ducks';
import { CRMFilesUploadRouteSkeleton } from './CRMFilesUploadRouteSkeleton';
import {
  bulkImportFromCsvFile,
  resetState,
  selectError,
  selectProcessedSoFar,
  selectProgress,
  selectUploading,
} from './ducks';
import { CRMFilesUploadSection } from './grid-content/CRMFilesUploadSection';
import { useInvestorExtensionSchema } from './hooks/useInvestorExtensionSchema';

const mapStateToProps = state => ({
  account: selectAccount(state),
  loading: selectLoading(state),
  error: selectError(state),
  crmIntegration: selectCrmIntegration(state),
  importResult: selectProgress(state),
  fileUploadError: selectError(state),
  fileUploading: selectUploading(state),
  processedSoFar: selectProcessedSoFar(state),
  selfSubdomain: selectSelfSubdomain(),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      bulkImportFromCsvFile,
      resetState,
    },
    dispatch
  ),
});

type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type StateProps = ReturnType<typeof mapStateToProps>;
type Props = StateProps & DispatchProps;
export type RouteParams = { crmIntegrationId?: UUID };

export const CRMFilesUploadRouteComponent: FC<Props> = ({
  account,
  loading,
  error,
  crmIntegration,
  importResult,
  actions,
  fileUploadError,
  fileUploading,
  processedSoFar,
  selfSubdomain,
}) => {
  const isSysAdmin = selfSubdomain === 'system';
  const investorExtensionSchema = useInvestorExtensionSchema({
    tenantId: crmIntegration?.accountId,
    isSysAdmin,
  });

  useDocumentTitle(
    account?.name
      ? routeFactory.customerCrmFilesUpload.getDocumentTitle({ accountName: account?.name })
      : routeFactory.sysAdminCrmFilesUpload.getDocumentTitle({
          crmIntegrationName: crmIntegration?.name,
        })
  );

  useEffect(() => {
    if (!processedSoFar.moreToUpload && !fileUploading && importResult.rowsProcessed > 0) {
      actions.resetState();
    }
  }, [actions, fileUploading, importResult.rowsProcessed, processedSoFar.moreToUpload]);

  if (loading) {
    return <CRMFilesUploadRouteSkeleton />;
  }

  if (error) {
    return <ServerErrorAlert error={error} title="Error" />;
  }

  if (selfSubdomain === 'system' && !crmIntegration) {
    return null;
  }

  return (
    <IdentityPageContent
      gridContent={
        <CRMFilesUploadSection
          investorExtensionHeaders={investorExtensionSchema.headers}
          importResult={importResult}
          onUpload={formValues => {
            if (crmIntegration && crmIntegration.subdomain) {
              actions.bulkImportFromCsvFile(formValues, crmIntegration.subdomain);
            } else {
              actions.bulkImportFromCsvFile(formValues);
            }
          }}
          uploadError={fileUploadError}
          isUploading={fileUploading}
          processedSoFar={processedSoFar}
        />
      }
    />
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(CRMFilesUploadRouteComponent);
